import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import HeaderCarousel from "@/components/HeaderCarousel";
import { Reveal } from "@/components/Reveal";
import { PAGE_HEADERS } from "@/data/site";
import {
  ShieldCheck, 
  FileText, 
  Compass, 
  Target, 
  Sparkles, 
  Users, 
  ArrowRight,
  Repeat,
  BookOpen, 
  Search, 
  Cpu,  
  Landmark, 
  GraduationCap, 
  Check, 
  HeartHandshake, 
  TrendingUp, 
  Globe,
  ChevronDown, 
  Layers,
  Award,
  Crown,
  Wallet,
  Briefcase
} from "lucide-react";

export default function About() {

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Find the element matching the hash (e.g. id="history")
      const element = document.getElementById(hash.replace("#", ""));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If no hash, scroll to top normally
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [hash]);

  const historyPolicies = [
    "Funding, Donations, and Sponsorship Policy",
    "Executive Members Travel Policy",
    "Policy Document on Sub-Groups Membership Regulations",
    "Advocacy Strategy & Insurance Indemnity Frameworks for Medical Professionals",
    "Strategic Purchasing & Community Infrastructure Support Guidelines",
  ];

  // Tier 1: Executive Office & Secretariat
  const president = { name: "Dr Hadzlinda Zainal", role: "President", dept: "Governance & Strategic Leadership" };
  const secretariat = [
    { name: "Ms Nurfarahin", role: "Secretary", dept: "Presidential Secretariat" },
    { name: "Ms Jemima Ruran", role: "Assistant Secretary", dept: "Presidential Secretariat" },
  ];

  // Tier 2: Deputy Leadership & Financial Directorate
  const deputies = [
    { name: "Mr R D", role: "Deputy President", dept: "Finance" },
    { name: "Ms Abryza Anuar", role: "Deputy President", dept: "Education" },
    { name: "Dr A", role: "Deputy President", dept: "Health" },
  ];

  const financeTeam = [
    { name: "Dr Abry Farah Hanim Anuar", role: "Treasurer", dept: "Fiscal Governance" },
    { name: "Mr Taufiq", role: "Assistant Treasurer", dept: "Fiscal Operations" },
  ];

  // Tier 3: Operational Command (Under CEO)
  const ceo = { name: "Ms Nazirah", role: "Chief Executive Officer (CEO)", dept: "Executive Management" };
  
  const ceoTeam = [
    { name: "Ms Fahada", role: "Digital Officer", dept: "Digital Health & Tech" },
    { name: "Ms Anis", role: "Digital Officer", dept: "Digital Health & Tech" },
    { name: "Ms Syuhadah", role: "Auditor", dept: "Internal Audit & Compliance" },
  ];

  // Tier 4: Advisory
  const advisors = [
    { name: "Dr Saravanan S R Sundaramurthy", role: "Advisor", dept: "Strategic Advisory" },
  ];

  const phiPhases = [
    { phase: "01", title: "Prenatal & Maternal Foundations", label: "Womb", body: "Establishing early health literacy, maternal guidance, and structural monitoring before birth." },
    { phase: "02", title: "Developmental Literacy", label: "Childhood & Youth", body: "Embedding core health habits and preventative literacy early through structured education and community outreach." },
    { phase: "03", title: "Active Ownership", label: "Adulthood", body: "Empowering individuals with continuous monitoring, data-driven self-care, and professional development." },
    { phase: "04", title: "Dignified Longevity", label: "Senior Care to Tomb", body: "Maintaining quality of life, cognitive health, and chronic disease management through sustained ecosystem support." },
  ];

  const sustainableLoopSteps = [
    { step: "01", title: "Evidence-Based Research", desc: "Rigorous clinical and statistical evaluation identifying core population needs." },
    { step: "02", title: "Digital Delivery via Helios", desc: "Friction-free distribution of verified health literacy into community hands." },
    { step: "03", title: "Community Action & Monitoring", desc: "Grassroots engagement producing measurable wellness outcomes and health tracking data." },
    { step: "04", title: "Policy & System Reinvestment", desc: "Translating data insights back into sustainable health infrastructure and institutional growth." },
  ];

  return (
    <div data-testid="about-page" className="text-slate-800 text-lg md:text-xl font-medium leading-relaxed">
      <HeaderCarousel testid="about-header" {...PAGE_HEADERS.about} />

      {/* ===================== 1. HISTORY ===================== */}
      <section id="history" className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-24 border-b border-slate-200">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <p className="font-mono text-base uppercase tracking-[0.25em] text-michma font-bold mb-4">
              Our Journey 🚀
            </p>
            <h2 className="font-heading font-extrabold text-5xl lg:text-6xl tracking-tight text-michma-ink">
              Institutional Roots & History
            </h2>
          </div>
        </Reveal>
        
        <div className="lg:col-span-7 space-y-12">
          <Reveal>
            <div className="border-l-4 border-michma pl-6 md:pl-8 space-y-4">
              <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold">
                01. Statutory Genesis & Institutional Roots
              </span>
              <h3 className="font-heading font-extrabold text-3xl md:text-4xl text-michma-ink">
                Bridging Gaps in Community Health Execution
              </h3>
              <p className="text-slate-700 leading-relaxed text-lg md:text-xl">
                Established under the statutory authority of the Registration of Societies (ROS) Malaysia in 2016, the Malaysian Integrated Community Health Medical Association (MICHMA) was founded as a dedicated non-governmental organization to bridge critical gaps in community health execution.
              </p>
              <p className="text-slate-700 leading-relaxed text-lg md:text-xl">
                From its inception, MICHMA was designed not merely as an advisory body, but as an operational force specialized in organizing, managing, and implementing evidence-based public health strategies.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="border-l-4 border-slate-400 pl-6 md:pl-8 space-y-4">
              <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold">
                02. Institutional Governance & Operational Integrity
              </span>
              <h3 className="font-heading font-extrabold text-3xl md:text-4xl text-michma-ink">
                Anchored in Transparency and Fiduciary Discipline
              </h3>
              <p className="text-slate-700 leading-relaxed text-lg md:text-xl">
                MICHMA’s longevity and institutional authority are anchored in a transparent, accountable governance framework.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className="bg-slate-50 hover:bg-white hover:shadow-lg transition-all p-6 rounded-3xl border-2 border-slate-200">
                  <div className="flex items-center gap-3 text-michma font-extrabold text-lg mb-3">
                    <Users className="w-6 h-6 text-michma shrink-0" /> Executive Accountability
                  </div>
                  <p className="text-base md:text-lg text-slate-700 leading-relaxed font-normal">
                    Governed by an elected Executive Committee serving three-year mandates, determined democratically at the AGM.
                  </p>
                </div>

                <div className="bg-slate-50 hover:bg-white hover:shadow-lg transition-all p-6 rounded-3xl border-2 border-slate-200">
                  <div className="flex items-center gap-3 text-michma font-extrabold text-lg mb-3">
                    <ShieldCheck className="w-6 h-6 text-michma shrink-0" /> Fiscal Rigor
                  </div>
                  <p className="text-base md:text-lg text-slate-700 leading-relaxed font-normal">
                    Maintains strict financial transparency with mandatory annual independent audits to ensure fiduciary compliance.
                  </p>
                </div>
              </div>

              <div className="pt-6">
                <p className="text-xl font-extrabold text-michma-ink mb-4">Policy Governance Infrastructure:</p>
                <ul className="space-y-3">
                  {historyPolicies.map((policy, idx) => (
                    <li key={idx} className="flex items-start gap-4 text-base md:text-lg text-slate-800 font-medium">
                      <FileText className="w-6 h-6 text-michma-coral shrink-0 mt-1" />
                      <span>{policy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-michma-soft p-8 md:p-10 rounded-3xl border-2 border-slate-300 shadow-sm">
              <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold block mb-2">
                Strategic Evolution 🌟
              </span>
              <h4 className="font-heading font-extrabold text-2xl md:text-3xl text-michma-ink mb-4">Moving Toward Infinitism</h4>
              <p className="text-slate-700 leading-relaxed text-base md:text-lg font-normal">
                As public health needs evolve in a digital, data-driven era, MICHMA has expanded its original operational framework into a global movement under the doctrine of Public Health Infinitism (PHI).
              </p>
            </div>
          </Reveal>
        </div>
      </section>

{/* ===================== 2. BRAND LOGO & HISTORICAL EVOLUTION ===================== */}
      <section id="logo" className="bg-white py-24 border-y border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16">
          
          {/* Section Header */}
          <Reveal>
            <div className="text-center max-w-3xl mx-auto space-y-3">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-michma font-extrabold block">
                Institutional Brand Maturity
              </span>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-michma-ink tracking-tight">
                Evolution of Our Brand Identity
              </h2>
              <p className="text-slate-700 text-lg md:text-xl leading-relaxed">
                Tracing MICHMA’s transformation from a traditional civil society association into an evidence-driven digital health ecosystem.
              </p>
            </div>
          </Reveal>

          {/* Evolution Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            
            {/* Card 1: 2016 Foundation */}
            <Reveal delay={0.1} className="h-full">
              <div className="bg-slate-50 border-2 border-slate-200 rounded-3xl p-6 h-full flex flex-col justify-between hover:shadow-lg transition-all">
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 flex items-center justify-center h-48 shadow-inner">
                    <img src="/oldlogoMichma-1.png" alt="MICHMA 2016 Foundational Crest" className="max-h-full object-contain" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">
                      2016 – Foundation Era
                    </span>
                    <h3 className="font-heading font-extrabold text-xl text-michma-ink mb-2">
                      Statutory Governance
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Anchored in traditional public health symbolism, reflecting ROS registration, statutory compliance, and structured medical oversight.
                    </p>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-200 font-mono text-xs text-slate-500 font-semibold">
                  Focus: Institutional Governance
                </div>
              </div>
            </Reveal>

            {/* Card 2: Transition Era */}
            <Reveal delay={0.2} className="h-full">
              <div className="bg-slate-50 border-2 border-slate-200 rounded-3xl p-6 h-full flex flex-col justify-between hover:shadow-lg transition-all">
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 flex items-center justify-center h-48 shadow-inner">
                    <img src="/oldlogoMichma-2.png" alt="MICHMA Community Care Emblem" className="max-h-full object-contain" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">
                      Transition Era
                    </span>
                    <h3 className="font-heading font-extrabold text-xl text-michma-ink mb-2">
                      Community Outreach
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Emphasized human-centric health promotion, grassroots involvement, and direct community care across local populations.
                    </p>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-200 font-mono text-xs text-slate-500 font-semibold">
                  Focus: Grassroots Engagement
                </div>
              </div>
            </Reveal>

            {/* Card 3: Present PHI Era */}
            <Reveal delay={0.3} className="h-full">
              <div className="bg-michma-soft/40 border-2 border-michma rounded-3xl p-6 h-full flex flex-col justify-between shadow-xl relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-michma text-white font-mono text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full z-10">
                  Current Identity
                </div>
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-6 border border-michma/20 flex items-center justify-center h-48 shadow-inner">
                    <img src="/logoMichma.png" alt="MICHMA Modern PHI Ecosystem Emblem" className="max-h-full object-contain" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-bold uppercase tracking-widest text-michma block mb-1">
                      Present – PHI Era
                    </span>
                    <h3 className="font-heading font-extrabold text-xl text-michma-ink mb-2">
                      Digital & Research Ecosystem
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Combines the infinity ribbon (womb-to-tomb care) with a data hexagon, representing gatekeeper-free health literacy and Helios tech integration.
                    </p>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-michma/20 font-mono text-xs text-michma font-bold">
                  Focus: Public Health Infinitism (PHI)
                </div>
              </div>
            </Reveal>

          </div>

          {/* Bottom Architecture Banner */}
          <Reveal>
            <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-3">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-michma font-bold block">
                  Flagship Identity Architecture
                </span>
                <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-white">
                  Designed for Global Health Scale
                </h3>
                <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                  Our current identity communicates to multilateral partners that MICHMA operates not merely as a traditional NGO, but as an advanced research, technology, and execution framework.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <div className="bg-slate-800/80 backdrop-blur p-4 rounded-2xl border border-slate-700/80">
                  <span className="font-mono text-xs text-michma font-bold block mb-0.5">Cyan & Teal Ribbon ♾️</span>
                  <span className="text-xs text-slate-300">Continuous lifelong womb-to-tomb care (PHI)</span>
                </div>
                <div className="bg-slate-800/80 backdrop-blur p-4 rounded-2xl border border-slate-700/80">
                  <span className="font-mono text-xs text-michma-coral font-bold block mb-0.5">Hexagonal Core ⬡</span>
                  <span className="text-xs text-slate-300">Data integrity, research precision, and Helios tech</span>
                </div>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ===================== 4. STRATEGIC FRAMEWORK ===================== */}
      <section id="framework" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-b border-slate-200">
        <Reveal>
          <div className="mb-16">
            <p className="font-mono text-sm uppercase tracking-[0.25em] text-michma font-extrabold mb-4">Strategic Framework</p>
            <h2 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight text-michma-ink">
              Vision, Mission & Core Purpose
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border-2 border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma hover:shadow-xl transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-michma-tint/80 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-michma font-extrabold">
                    Vision
                  </span>
                  <Compass className="w-8 h-8 text-michma" />
                </div>
                <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-4">
                  Gatekeeper-Free Rights
                </h3>
                <p className="text-slate-700 leading-relaxed text-base md:text-lg">
                  To lead a global movement where health literacy and ownership are continuous, gatekeeper-free rights, transforming population health into sustainable national capital from womb to tomb.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="h-full rounded-3xl border-2 border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma hover:shadow-xl transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-michma-coral font-extrabold">
                    Mission
                  </span>
                  <Target className="w-8 h-8 text-michma-coral" />
                </div>
                <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-4">
                  Eliminating Institutional Barriers
                </h3>
                <p className="text-slate-700 leading-relaxed text-base md:text-lg">
                  To eliminate institutional barriers in healthcare by leveraging digital innovation, evidence-based research, and community-driven initiatives, delivering friction-free health literacy.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="h-full rounded-3xl border-2 border-slate-200 bg-white p-8 md:p-10 flex flex-col justify-between hover:border-michma hover:shadow-xl transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-8">
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-slate-800 font-extrabold">
                    Core Purpose
                  </span>
                  <Sparkles className="w-8 h-8 text-slate-700" />
                </div>
                <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-4">
                  Lifelong Ecosystem
                </h3>
                <p className="text-slate-700 leading-relaxed text-base md:text-lg">
                  To redefine public health from an episodic, sickness-driven intervention into a lifelong, self-sustaining ecosystem where every individual possesses the knowledge and tools to thrive.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== 5. THE PHI DOCTRINE ===================== */}
      <section id="phiDoctrine" className="bg-slate-900 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl mb-16">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/20 px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] text-michma-coral font-extrabold mb-6">
                Core Philosophy 💡
              </span>
              <h2 className="font-heading font-extrabold text-5xl md:text-6xl tracking-tight text-white mb-6">
                The PHI Doctrine
              </h2>
              <p className="font-mono text-base uppercase tracking-widest text-michma font-bold mb-4">Womb-to-Tomb Continuity</p>
              <p className="text-slate-200 text-xl md:text-2xl leading-relaxed font-normal">
                Public Health Infinitism (PHI) views human health not as isolated medical events, but as an unbroken, lifelong journey.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {phiPhases.map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="h-full bg-slate-800 rounded-3xl p-8 border-2 border-slate-700 hover:border-michma hover:scale-[1.02] transition-all">
                  <span className="font-mono text-sm uppercase tracking-[0.2em] text-michma font-extrabold mb-2 block">Phase {item.phase}</span>
                  <span className="text-xs font-mono uppercase tracking-wider text-michma-coral bg-michma-coral/20 px-3 py-1 rounded-full font-bold inline-block mb-4">
                    {item.label}
                  </span>
                  <h3 className="font-heading font-extrabold text-2xl text-white mb-3">{item.title}</h3>
                  <p className="text-slate-300 text-base leading-relaxed">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== 6. THE SUSTAINABLE LOOP ===================== */}
      <section id="loop" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 border-b border-slate-200">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-mono text-sm uppercase tracking-[0.25em] text-michma font-extrabold mb-4">
                Self-Sustaining Impact 🔄
              </p>
              <h2 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight text-michma-ink max-w-2xl">
                The Sustainable Loop
              </h2>
            </div>
            <div className="flex items-center gap-2 text-michma font-mono text-sm font-extrabold uppercase tracking-widest bg-michma-soft px-4 py-2 rounded-full">
              <Repeat className="w-5 h-5 animate-spin-slow" /> Infinite Cycle
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sustainableLoopSteps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="p-8 rounded-3xl bg-slate-50 border-2 border-slate-200 hover:bg-white hover:shadow-lg transition-all h-full flex flex-col justify-between group">
                <div>
                  <span className="font-mono text-3xl font-extrabold text-michma block mb-4">{s.step}</span>
                  <h3 className="font-heading font-extrabold text-2xl text-michma-ink mb-3">{s.title}</h3>
                  <p className="text-base text-slate-700 leading-relaxed">{s.desc}</p>
                </div>
                <div className="pt-6 mt-6 border-t border-slate-200 flex items-center gap-2 text-sm font-mono font-extrabold text-michma group-hover:translate-x-2 transition-transform">
                  <span>Cycle Flow</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== 7. 6 PILLARS OF OUR FUTURE ===================== */}
      <section id="pillar" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="font-mono text-sm uppercase tracking-[0.25em] text-michma font-extrabold mb-3">
                Strategic Directions 🎯
              </p>
              <h2 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight text-michma-ink">
                6 Pillars of Our Future
              </h2>
              <p className="text-xl font-bold text-slate-700 mt-4">
                Empowering People. Strengthening Communities. Transforming Health.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Pillar 01 */}
            <Reveal delay={0.05}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg">
                      <BookOpen className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-blue-600">01</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Health Literacy
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Empowering individuals and communities with knowledge to make informed health choices.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Health Education", "Health Promotion", "Digital Health Literacy", "Children & Youth", "Family & Community"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-blue-600 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Pillar 02 */}
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg">
                      <Search className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-emerald-600">02</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Research & Evidence
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Generating and translating evidence to guide actions and improve health outcomes.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Public Health Research", "Health Literacy Research", "Data & Analytics", "Monitoring & Evaluation", "Publications"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-600 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Pillar 03 */}
            <Reveal delay={0.15}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-cyan-600 text-white flex items-center justify-center shadow-lg">
                      <Cpu className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-cyan-600">03</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Digital Health & Innovation
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Harnessing technology to create accessible, engaging and impactful health solutions.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Digital Platforms (HELIOS)", "AI & Emerging Tech", "Health Information Systems", "Innovation Labs", "Digital Inclusion"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-cyan-600 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Pillar 04 */}
            <Reveal delay={0.2}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-amber-600 text-white flex items-center justify-center shadow-lg">
                      <Users className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-amber-600">04</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Community Health
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Working with communities to build healthier, resilient and empowered societies.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Community Engagement", "Outreach & Campaigns", "Schools & Families", "Vulnerable Populations", "Grassroots Initiatives"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-amber-600 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Pillar 05 */}
            <Reveal delay={0.25}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-purple-600 text-white flex items-center justify-center shadow-lg">
                      <Landmark className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-purple-600">05</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Policy & Systems
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Influencing policies and strengthening systems for equitable and sustainable health.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Policy Advocacy", "Evidence-to-Policy", "Health System Strengthening", "Multi-sector Collaboration", "Governance"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-purple-600 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Pillar 06 */}
            <Reveal delay={0.3}>
              <div className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-8 h-full flex flex-col justify-between hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-700 text-white flex items-center justify-center shadow-lg">
                      <GraduationCap className="w-8 h-8" />
                    </div>
                    <span className="font-mono font-extrabold text-2xl text-indigo-700">06</span>
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight text-michma-ink mb-3">
                    Capacity Building
                  </h3>
                  <p className="text-base text-slate-700 leading-relaxed mb-6">
                    Developing the people and organisations that drive health impact today and tomorrow.
                  </p>
                </div>
                <ul className="space-y-3 pt-6 border-t-2 border-slate-200 text-base font-semibold text-slate-800">
                  {["Training & Workshops", "Professional Development", "Leadership Development", "Knowledge Exchange", "Train-the-Trainer"].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-indigo-700 shrink-0 stroke-[3]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Bottom Strategic Objectives Banner */}
          <Reveal delay={0.35}>
            <div className="mt-16 bg-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div className="flex flex-col items-center hover:scale-110 transition-transform">
                  <Users className="w-10 h-10 text-michma mb-3" />
                  <span className="font-heading font-extrabold text-sm md:text-base uppercase tracking-wider">People at the Centre</span>
                </div>
                <div className="flex flex-col items-center hover:scale-110 transition-transform">
                  <HeartHandshake className="w-10 h-10 text-michma mb-3" />
                  <span className="font-heading font-extrabold text-sm md:text-base uppercase tracking-wider">Partnerships for Impact</span>
                </div>
                <div className="flex flex-col items-center hover:scale-110 transition-transform">
                  <TrendingUp className="w-10 h-10 text-michma mb-3" />
                  <span className="font-heading font-extrabold text-sm md:text-base uppercase tracking-wider">Sustainable Scaling</span>
                </div>
                <div className="flex flex-col items-center hover:scale-110 transition-transform">
                  <Globe className="w-10 h-10 text-michma mb-3" />
                  <span className="font-heading font-extrabold text-sm md:text-base uppercase tracking-wider">Global Reach</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}